import { useState, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
} from '@mui/material'
import { Send, SkipNext, Cancel, Close, Lightbulb } from '@mui/icons-material'
import { SessionNodeDto, PendingInputDto } from '@api/types/session'
import { QuestionAnswerType } from '../../../types/workflow.types'
import { useSessionInput } from '../../../hooks/useSessionInput'

interface QuestionAnswerInputProps {
  node: SessionNodeDto
  pendingInput: PendingInputDto
  sessionId: string | null
  onSubmitSuccess?: () => void
  onClose?: () => void
}

const LETTER_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']

export default function QuestionAnswerInput({
  node,
  pendingInput,
  sessionId,
  onSubmitSuccess,
  onClose,
}: QuestionAnswerInputProps) {
  const [answer, setAnswer] = useState('')
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const hasCalledSuccessRef = useRef(false)

  const { submitAnswer, skipChallenge, cancelChallenge, isSubmitting, isSuccess } = useSessionInput(sessionId)

  useEffect(() => {
    if (isSuccess && onSubmitSuccess && !hasCalledSuccessRef.current) {
      hasCalledSuccessRef.current = true
      onSubmitSuccess()
    }
  }, [isSuccess, onSubmitSuccess])

  const answerType = node.answerType as QuestionAnswerType | undefined
  const options = node.answerOptions || []
  const isMultiSelect = answerType === QuestionAnswerType.MultiSelect
  const isMultipleChoice = answerType === QuestionAnswerType.MultipleChoice
  const isFreeText = answerType === QuestionAnswerType.FreeText || (!isMultiSelect && !isMultipleChoice)

  const handleSubmit = () => {
    if (!node.id) return
    let value: string
    if (isFreeText) {
      value = answer.trim()
    } else if (isMultiSelect) {
      value = selectedOptions.join(',')
    } else {
      value = selectedOptions[0] || ''
    }
    if (!value && pendingInput.required) return
    submitAnswer(node.id, value)
  }

  const handleSkip = () => {
    if (!node.id || !pendingInput.allowSkip) return
    skipChallenge(node.id, pendingInput.inputType)
  }

  const handleCancel = () => {
    if (!node.id || !pendingInput.allowCancel) return
    cancelChallenge(node.id, pendingInput.inputType)
  }

  const handleOptionChange = (optionId: string) => {
    if (isMultiSelect) {
      setSelectedOptions((prev) =>
        prev.includes(optionId)
          ? prev.filter((id) => id !== optionId)
          : [...prev, optionId]
      )
    } else {
      setSelectedOptions([optionId])
    }
  }

  const canSubmit = isFreeText
    ? answer.trim().length > 0 || !pendingInput.required
    : selectedOptions.length > 0 || !pendingInput.required

  const questionText = node.questionText || node.description || ''

  // Free text input - chat-style row
  if (isFreeText) {
    return (
      <Box
        sx={{
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
          borderTop: 1,
          borderColor: 'divider',
          p: 2,
          position: 'relative',
        }}
      >
        {onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ position: 'absolute', top: 4, right: 4, color: 'text.disabled' }}
          >
            <Close fontSize="small" />
          </IconButton>
        )}

        {/* Question text */}
        {questionText && (
          <Typography
            sx={{
              fontSize: '0.875rem',
              lineHeight: 1.6,
              color: 'text.secondary',
              mb: 1.5,
              pr: onClose ? 4 : 0,
            }}
          >
            {questionText}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          {/* Hint button */}
          {pendingInput.allowSkip && (
            <Box
              onClick={handleSkip}
              sx={{
                width: 42,
                height: 42,
                borderRadius: '50%',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(66,153,225,0.1)' : 'rgba(66,153,225,0.08)',
                border: 1,
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(66,153,225,0.25)' : 'rgba(66,153,225,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
                color: 'info.main',
                '&:hover': { bgcolor: 'rgba(66,153,225,0.15)' },
              }}
            >
              <Lightbulb sx={{ fontSize: 20 }} />
            </Box>
          )}

          {/* Text input field */}
          <Box
            component="input"
            type="text"
            value={answer}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAnswer(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' && canSubmit) handleSubmit() }}
            placeholder="Enter your answer..."
            disabled={isSubmitting}
            sx={{
              flex: 1,
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(21,32,58,0.6)' : 'rgba(0,0,0,0.04)',
              border: 1,
              borderColor: 'divider',
              borderRadius: '24px',
              px: 2,
              py: 1.4,
              fontSize: '0.875rem',
              color: 'text.primary',
              fontFamily: "'Inter', sans-serif",
              outline: 'none',
              '&:focus': {
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.4)' : 'primary.main',
              },
              '&::placeholder': {
                color: 'text.disabled',
              },
              '&:disabled': {
                opacity: 0.5,
              },
            }}
          />

          {/* Submit button */}
          <ButtonBase
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              background: canSubmit
                ? 'linear-gradient(135deg, #d4a853, #c4943f)'
                : 'rgba(128,128,128,0.2)',
              color: canSubmit ? '#1a1a2e' : 'text.disabled',
              flexShrink: 0,
              boxShadow: canSubmit ? '0 2px 8px rgba(212,168,83,0.3)' : 'none',
              transition: 'all 0.15s',
              '&:hover': canSubmit ? {
                boxShadow: '0 4px 12px rgba(212,168,83,0.4)',
              } : {},
            }}
          >
            {isSubmitting ? <CircularProgress size={20} sx={{ color: '#1a1a2e' }} /> : <Send sx={{ fontSize: 18 }} />}
          </ButtonBase>
        </Box>

        {/* Cancel button if enabled */}
        {pendingInput.allowCancel && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Button
              size="small"
              startIcon={<Cancel />}
              onClick={handleCancel}
              disabled={isSubmitting}
              sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
            >
              {pendingInput.cancelButtonText || 'Cancel'}
            </Button>
          </Box>
        )}
      </Box>
    )
  }

  // Multiple choice / Multi-select
  return (
    <Box
      sx={{
        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
        borderTop: 1,
        borderColor: 'divider',
        p: 2,
        position: 'relative',
      }}
    >
      {onClose && (
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', top: 4, right: 4, color: 'text.disabled' }}
        >
          <Close fontSize="small" />
        </IconButton>
      )}

      {/* Question text */}
      {questionText && (
        <Typography
          sx={{
            fontSize: '0.875rem',
            lineHeight: 1.6,
            color: 'text.secondary',
            mb: 1.5,
            pr: onClose ? 4 : 0,
          }}
        >
          {questionText}
        </Typography>
      )}

      {/* Label */}
      <Typography
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
          color: 'text.disabled',
          mb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        {isMultiSelect ? '☑ Select all that apply' : '✍ Choose one answer'}
      </Typography>

      {/* Hint for multi-select */}
      {isMultiSelect && (
        <Typography
          sx={{
            textAlign: 'center',
            fontSize: '0.6875rem',
            color: 'text.disabled',
            mb: 1.5,
          }}
        >
          Tap each answer you want to include
        </Typography>
      )}

      {/* Options */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.75 }}>
        {options.map((option, index) => {
          const isSelected = selectedOptions.includes(option.id)
          return (
            <ButtonBase
              key={option.id}
              onClick={() => handleOptionChange(option.id)}
              disabled={isSubmitting}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                bgcolor: isSelected
                  ? (theme) => theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.08)' : 'rgba(212,168,83,0.06)'
                  : (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'background.paper',
                border: 1.5,
                borderColor: isSelected ? 'primary.main' : 'divider',
                borderRadius: 3,
                px: 2,
                py: 1.75,
                width: '100%',
                textAlign: 'left',
                transition: 'all 0.15s',
                '&:hover': {
                  borderColor: isSelected ? 'primary.main' : (theme) => theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.3)' : 'rgba(0,0,0,0.2)',
                  bgcolor: isSelected ? undefined : (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                },
              }}
            >
              {/* Radio / Checkbox indicator */}
              {isMultiSelect ? (
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: 1.5,
                    border: 2,
                    borderColor: isSelected ? 'primary.main' : 'text.disabled',
                    bgcolor: isSelected ? 'primary.main' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.15s',
                  }}
                >
                  {isSelected && (
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 800, color: (theme) => theme.palette.mode === 'dark' ? '#1a1a2e' : '#fff', lineHeight: 1 }}>
                      ✓
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    border: 2,
                    borderColor: isSelected ? 'primary.main' : 'text.disabled',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.15s',
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      transform: isSelected ? 'scale(1)' : 'scale(0)',
                      transition: 'transform 0.15s',
                    }}
                  />
                </Box>
              )}

              {/* Option label */}
              <Typography
                sx={{
                  flex: 1,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'text.primary',
                }}
              >
                {option.text}
              </Typography>

              {/* Letter badge */}
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 1.5,
                  bgcolor: isSelected
                    ? (theme) => theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.1)' : 'rgba(212,168,83,0.08)'
                    : 'rgba(128,128,128,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: isSelected ? 'primary.main' : 'text.disabled',
                  }}
                >
                  {LETTER_LABELS[index] || ''}
                </Typography>
              </Box>
            </ButtonBase>
          )
        })}
      </Box>

      {/* Submit button */}
      <ButtonBase
        onClick={handleSubmit}
        disabled={!canSubmit || isSubmitting}
        sx={{
          width: '100%',
          background: canSubmit
            ? 'linear-gradient(135deg, #d4a853, #c4943f)'
            : 'rgba(128,128,128,0.2)',
          border: 'none',
          borderRadius: 3,
          color: canSubmit ? '#1a1a2e' : 'text.disabled',
          fontSize: '0.9375rem',
          fontWeight: 700,
          py: 1.75,
          fontFamily: "'Inter', sans-serif",
          boxShadow: canSubmit ? '0 2px 10px rgba(212,168,83,0.3)' : 'none',
          transition: 'all 0.15s',
          '&:hover': canSubmit ? {
            boxShadow: '0 4px 16px rgba(212,168,83,0.4)',
          } : {},
        }}
      >
        {isSubmitting ? (
          <CircularProgress size={20} sx={{ color: '#1a1a2e' }} />
        ) : isMultiSelect ? (
          `Submit (${selectedOptions.length} selected)`
        ) : (
          'Submit Answer'
        )}
      </ButtonBase>

      {/* Skip / Cancel */}
      {(pendingInput.allowCancel || pendingInput.allowSkip) && (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1.5 }}>
          {pendingInput.allowCancel && (
            <Button size="small" startIcon={<Cancel />} onClick={handleCancel} disabled={isSubmitting} sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {pendingInput.cancelButtonText || 'Cancel'}
            </Button>
          )}
          {pendingInput.allowSkip && (
            <Button size="small" startIcon={<SkipNext />} onClick={handleSkip} disabled={isSubmitting} sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {pendingInput.skipButtonText || 'Skip'}
            </Button>
          )}
        </Box>
      )}
    </Box>
  )
}
